import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../../supabaseClient";
import { getSupabaseErrorMessage } from "./adminConfig";

const CORE_COLUMNS = [
  "name",
  "brand",
  "set_name",
  "subset",
  "year",
  "year_end",
  "category_id",
  "subcategory_id",
  "category_name",
  "subcategory_name",
  "card_number",
  "release_key",
  "release_id",
];

const HEADER_ALIASES = {
  card: "card_number",
  card_no: "card_number",
  card_num: "card_number",
  card_number: "card_number",
  cardnumber: "card_number",
  card_: "card_number",
  set: "set_name",
  setname: "set_name",
  set_name: "set_name",
  subset: "subset",
  sub_set: "subset",
  sub_set_name: "subset",
  insert: "subset",
  category: "category_name",
  subcategory: "subcategory_name",
};

const STAGE_CHUNK_SIZE = 250;
const HISTORY_LIMIT = 12;
const POLL_MS = 4000;
const ACTIVE_STATUSES = new Set(["staging", "queued", "processing"]);
const PACKAGE_SHEETS = {
  release: "Release",
  family: "Family",
  releases: "Releases",
  canonical: "Canonical Items",
  variants: "Variants",
  applicability: "Variant Applicability",
  qa: "QA",
  sources: "Sources",
};

function findSheetName(workbook, desiredName) {
  const wanted = normalizeText(desiredName);
  return workbook.SheetNames.find((name) => normalizeText(name) === wanted) || null;
}

function sheetRows(workbook, sheetName) {
  if (!sheetName || !workbook.Sheets[sheetName]) return [];
  return XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: false, defval: "" });
}

function parseReleaseSheet(workbook, sheetName) {
  const rows = sheetRows(workbook, sheetName);
  const release = {};

  rows.forEach((row) => {
    const field = cleanHeader(row.field);
    if (!field || field === "field") return;
    release[field] = row.value;
  });

  return {
    ...release,
    release_key: String(release.release_key || "").trim(),
    name: String(release.name || "").trim(),
    family_key: String(release.family_key || release.release_family_key || "").trim(),
    family_name: String(release.family_name || release.release_family_name || "").trim(),
    product_name: String(release.product_name || "").trim(),
    release_name: String(release.release_name || "").trim(),
    display_release_name: ["true", "yes", "1"].includes(normalizeText(release.display_release_name)),
    display_release_name_supplied: String(release.display_release_name ?? "").trim() !== "",
    brand: String(release.brand || release.manufacturer || "").trim(),
    set_name: String(release.set_name || "").trim(),
    year: cleanYear(release.year),
    year_end: cleanYear(release.year_end),
    category_name: String(release.category_name || "").trim(),
    subcategory_name: String(release.subcategory_name || "").trim(),
    is_multi_sport: ["true", "yes", "1"].includes(normalizeText(release.is_multi_sport)),
  };
}

function parseFamilySheet(workbook, sheetName) {
  const rows = sheetRows(workbook, sheetName);
  const family = {};
  rows.forEach((row) => {
    const field = cleanHeader(row.field);
    if (!field || field === "field") return;
    family[field] = row.value;
  });
  return {
    ...family,
    family_key: String(family.family_key || family.release_family_key || "").trim(),
    family_name: String(family.family_name || family.release_family_name || family.name || "").trim(),
    product_name: String(family.product_name || "").trim(),
    brand: String(family.brand || family.manufacturer || "").trim(),
    year: cleanYear(family.year),
    year_end: cleanYear(family.year_end),
    category_name: String(family.category_name || "").trim(),
    subcategory_name: String(family.subcategory_name || "").trim(),
    is_multi_sport: ["true", "yes", "1"].includes(normalizeText(family.is_multi_sport)),
  };
}

function parseReleasesSheet(workbook, sheetName) {
  return sheetRows(workbook, sheetName).map((row) => ({
    release_key: String(row.release_key || row.key || "").trim(),
    release_name: String(row.release_name || row.name || "").trim(),
    set_name: String(row.set_name ?? "").trim(),
    subset: String(row.subset ?? "").trim(),
    canonical_identity_supplied: Object.keys(row).some((key) => cleanHeader(key) === "set_name") && Object.keys(row).some((key) => cleanHeader(key) === "subset"),
    display_release_name: ["true", "yes", "1"].includes(normalizeText(row.display_release_name)),
    display_release_name_supplied: String(row.display_release_name ?? "").trim() !== "",
  }));
}

// Validate declared collector-facing identity; do not derive it from a release label.
function validateCanonicalIdentity(rawRows, releases, { strict = false } = {}) {
  const problems = [];
  const byKey = new Map(releases.map((release) => [normalizeText(release.release_key), release]));
  for (const release of releases) {
    if (strict && (!release.canonical_identity_supplied || !release.set_name)) {
      problems.push(`Release ${release.release_key}: explicit set_name and subset columns are required (subset may be blank).`);
    }
  }
  rawRows.forEach((raw, index) => {
    const row = normalizeRow(raw);
    const release = byKey.get(normalizeText(row.release_key));
    if (!release) return; // Existing release-key validation handles this.
    if (strict && !Object.keys(raw).some((key) => cleanHeader(key) === "set_name")) {
      problems.push(`Canonical Items row ${index + 2}: set_name column is missing.`);
    }
    if (!row.set_name) problems.push(`Canonical Items row ${index + 2}: set_name is blank.`);
    if (release.set_name && normalizeText(row.set_name) !== normalizeText(release.set_name)) {
      problems.push(`Canonical Items row ${index + 2} (${release.release_key}): set_name "${row.set_name}" differs from declared "${release.set_name}".`);
    }
    if (strict && normalizeText(row.subset) !== normalizeText(release.subset)) {
      problems.push(`Canonical Items row ${index + 2} (${release.release_key}): subset "${row.subset || "(none)"}" differs from declared "${release.subset || "(none)"}".`);
    }
  });
  if (problems.length) {
    throw new Error(`Canonical identity validation failed (${problems.length} issue(s)). ${problems.slice(0, 8).join(" ")}${problems.length > 8 ? " Fix the remaining rows as well." : ""}`);
  }
}

async function resolveReleaseTaxonomy(release) {
  const probe = await resolveTaxonomyForRows([{
    category_id: cleanUuid(release.category_id),
    subcategory_id: cleanUuid(release.subcategory_id),
    category_name: release.category_name,
    subcategory_name: release.subcategory_name,
  }]);

  return {
    ...release,
    category_id: probe[0]?.category_id || null,
    subcategory_id: probe[0]?.subcategory_id || null,
  };
}

async function preflightPackageFamily(release) {
  // Explicit V4 product identity: use the authenticated resolver in dry-run mode.
  // Legacy V3/V3.1 packages without product_name retain their original preflight.
  if (release.product_name) {
    const { data, error } = await supabase.rpc("catalog_import_resolve_family_v4", {
      p_category_id: release.category_id,
      p_subcategory_id: release.is_multi_sport ? null : release.subcategory_id,
      p_year: release.year,
      p_year_end: release.year_end,
      p_brand: release.brand,
      p_family_name: release.family_name,
      p_product_name: release.product_name,
      p_family_key: release.family_key || null,
      p_is_multi_sport: Boolean(release.is_multi_sport),
      p_allow_create: false,
    });
    if (error) throw error;
    if (data?.action === "matched") return { ...data.preflight, status: "match", family_id: data.family_id, v4: data };
    if (data?.action === "would_create") return { ...data.preflight, status: "create", v4: data };
    return { ...data?.preflight, status: "review", message: data?.message || "V4 family identity requires review.", v4: data };
  }
  const { data, error } = await supabase.rpc("catalog_import_preflight_family", {
    p_category_id: release.category_id,
    p_subcategory_id: release.is_multi_sport ? null : release.subcategory_id,
    p_year: release.year,
    p_year_end: release.year_end,
    p_brand: release.brand,
    p_family_name: release.family_name,
    p_family_key: release.family_key || null,
    p_is_multi_sport: Boolean(release.is_multi_sport),
  });

  if (error) throw error;
  return data || {};
}

async function preflightPackageRelease(release, familyPreflight) {
  if (familyPreflight?.status === "review") {
    return { status: "review", message: "Resolve the release family before resolving its release." };
  }

  if (familyPreflight?.status !== "match" || !familyPreflight?.family_id) {
    return {
      status: "create",
      deferred: true,
      message: "The release will be created inside the new family after the family is resolved.",
    };
  }

  const { data, error } = await supabase.rpc("catalog_import_preflight_family_release", {
    p_release_family_id: familyPreflight.family_id,
    p_release_name: release.release_name,
  });

  if (error) throw error;
  return data || {};
}


function cleanHeader(header) {
  const normalized = String(header || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return HEADER_ALIASES[normalized] || normalized;
}

function cleanUuid(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(trimmed) ? trimmed : null;
}

function cleanYear(value) {
  if (value === null || value === undefined || value === "") return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : null;
}

// IMPORTANT: card numbers are identity strings, never numbers.
// This preserves values such as US1, 90A-AD, APC-MN, 001, etc.
function cleanCardNumber(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function parseJsonMaybe(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return { ...value };

  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function formatAdditionalDetailKey(key) {
  return key
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function normalizeRow(row) {
  const cleaned = {};

  Object.keys(row || {}).forEach((key) => {
    cleaned[cleanHeader(key)] = row[key];
  });

  const additionalDetails = parseJsonMaybe(cleaned.additional_details);

  Object.keys(cleaned).forEach((key) => {
    if (CORE_COLUMNS.includes(key) || key === "additional_details") return;
    if (cleaned[key] === "" || cleaned[key] === null || cleaned[key] === undefined) return;
    additionalDetails[formatAdditionalDetailKey(key)] = cleaned[key];
  });

  return {
    name: String(cleaned.name || "").trim(),
    brand: String(cleaned.brand || "").trim(),
    set_name: String(cleaned.set_name || "").trim(),
    subset: String(cleaned.subset || "").trim(),
    year: cleanYear(cleaned.year),
    year_end: cleanYear(cleaned.year_end),
    category_id: cleanUuid(cleaned.category_id),
    subcategory_id: cleanUuid(cleaned.subcategory_id),
    category_name: String(cleaned.category_name || "").trim(),
    subcategory_name: String(cleaned.subcategory_name || "").trim(),
    card_number: cleanCardNumber(cleaned.card_number),
    release_key: String(cleaned.release_key || "").trim(),
    release_id: cleaned.release_id ? Number(cleaned.release_id) : null,
    additional_details: additionalDetails,
  };
}

async function resolveTaxonomyForRows(rows) {
  const taxonomyCache = new Map();
  const resolvedRows = [];

  for (const row of rows) {
    if (row.category_id && row.subcategory_id) {
      resolvedRows.push(row);
      continue;
    }

    const categoryName = String(row.category_name || "").trim();
    const subcategoryName = String(row.subcategory_name || "").trim();

    if (!categoryName || !subcategoryName) {
      resolvedRows.push(row);
      continue;
    }

    const cacheKey = `${normalizeText(categoryName)}|${normalizeText(subcategoryName)}`;
    let resolved = taxonomyCache.get(cacheKey);

    if (!resolved) {
      const { data: categories, error: categoryError } = await supabase
        .from("categories")
        .select("id, name")
        .eq("name", categoryName)
        .limit(2);

      if (categoryError) throw categoryError;

      if ((categories || []).length !== 1) {
        throw new Error(
          `Expected exactly one category named "${categoryName}", found ${(categories || []).length}.`,
        );
      }

      const categoryId = categories[0].id;
      const { data: subcategories, error: subcategoryError } = await supabase
        .from("subcategories")
        .select("id, name, category_id")
        .eq("category_id", categoryId)
        .eq("name", subcategoryName)
        .limit(2);

      if (subcategoryError) throw subcategoryError;

      if ((subcategories || []).length !== 1) {
        throw new Error(
          `Expected exactly one ${categoryName} subcategory named "${subcategoryName}", found ${(subcategories || []).length}.`,
        );
      }

      resolved = {
        category_id: categoryId,
        subcategory_id: subcategories[0].id,
      };
      taxonomyCache.set(cacheKey, resolved);
    }

    resolvedRows.push({
      ...row,
      category_id: row.category_id || resolved.category_id,
      subcategory_id: row.subcategory_id || resolved.subcategory_id,
    });
  }

  return resolvedRows;
}

function getCanonicalVariation(row) {
  const details = row?.additional_details || {};
  const variationEntry = Object.entries(details).find(
    ([key]) => normalizeText(key).replace(/[_\s-]+/g, "") === "variation"
  );
  return variationEntry ? variationEntry[1] : null;
}

function buildImportIdentityKey(row) {
  return [
    row.year ?? "",
    normalizeText(row.release_key || row.release_id),
    normalizeText(row.name),
    normalizeText(row.brand),
    normalizeText(row.set_name),
    normalizeText(row.subset),
    normalizeText(row.card_number),
    normalizeText(getCanonicalVariation(row)),
  ].join("|");
}

function getPreflight(rows) {
  const seen = new Map();
  let missingName = 0;
  let missingYear = 0;
  let missingCardNumber = 0;
  let missingCategory = 0;
  let missingSubcategory = 0;
  let numericCardNumbers = 0;
  let alphanumericCardNumbers = 0;
  let invalidYearRanges = 0;

  rows.forEach((row, index) => {
    if (!row.name) missingName += 1;
    if (row.year === null || row.year === undefined) missingYear += 1;
    if (!row.category_id) missingCategory += 1;
    if (!row.subcategory_id) missingSubcategory += 1;

    if (row.year_end !== null && row.year_end !== undefined && row.year !== null && row.year !== undefined && row.year_end < row.year) invalidYearRanges += 1;

    if (!row.card_number) {
      missingCardNumber += 1;
    } else if (/^\d+$/.test(row.card_number)) {
      numericCardNumbers += 1;
    } else {
      alphanumericCardNumbers += 1;
    }

    const key = buildImportIdentityKey(row);
    const existing = seen.get(key) || [];
    existing.push(index + 2);
    seen.set(key, existing);
  });

  const duplicateGroups = Array.from(seen.entries())
    .filter(([, rowNumbers]) => rowNumbers.length > 1)
    .map(([key, rowNumbers]) => ({ key, rowNumbers }));

  return {
    total: rows.length,
    missingName,
    missingYear,
    missingCardNumber,
    missingCategory,
    missingSubcategory,
    numericCardNumbers,
    alphanumericCardNumbers,
    invalidYearRanges,
    duplicateGroups,
    duplicateRows: duplicateGroups.reduce((sum, group) => sum + group.rowNumbers.length, 0),
  };
}

function chunkArray(values, size) {
  const chunks = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
}

function stripTaxonomyNames(row) {
  const { category_name, subcategory_name, ...databaseRow } = row;
  return databaseRow;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusLabel(status) {
  const labels = {
    staging: "Uploading",
    queued: "Queued",
    processing: "Processing",
    completed: "Complete",
    needs_review: "Needs Review",
    failed: "Failed",
    cancelled: "Cancelled",
  };
  return labels[status] || status;
}

function jobProgress(job) {
  if (!job) return 0;
  if (job.status === "staging") {
    return job.total_rows ? Math.round((job.staged_rows / job.total_rows) * 100) : 0;
  }
  return Number(job.progress_percent || 0);
}

export default function BatchImportManager() {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [stageProgress, setStageProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeJob, setActiveJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [reviewRows, setReviewRows] = useState([]);
  const [importMode, setImportMode] = useState("canonical");
  const [packageData, setPackageData] = useState(null);
  const [familyPreflight, setFamilyPreflight] = useState(null);
  const [releasePreflight, setReleasePreflight] = useState(null);

  const previewRows = useMemo(() => rows.slice(0, 8), [rows]);
  const preflight = useMemo(() => getPreflight(rows), [rows]);

  const hasBlockingPreflightIssues =
    preflight.missingName > 0 ||
    preflight.missingCategory > 0 ||
    preflight.missingSubcategory > 0 ||
    preflight.invalidYearRanges > 0 ||
    preflight.duplicateGroups.length > 0 ||
    (importMode === "package" && (
      !familyPreflight ||
      !["match", "create"].includes(familyPreflight.status) ||
      !releasePreflight ||
      !["match", "create"].includes(releasePreflight.status)
    ));

  const loadHistory = useCallback(async () => {
    const { data, error } = await supabase
      .from("catalog_import_job_summary")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(HISTORY_LIMIT);

    if (error) {
      // Before the migration is installed this table will not exist.
      console.warn("Unable to load catalog import history:", error);
      return;
    }

    const jobs = data || [];
    setHistory(jobs);

    setActiveJob((current) => {
      if (current?.id) {
        return jobs.find((job) => job.id === current.id) || current;
      }
      return jobs.find((job) => ACTIVE_STATUSES.has(job.status)) || null;
    });
  }, []);

  const loadReviewRows = useCallback(async (jobId) => {
    if (!jobId) {
      setReviewRows([]);
      return;
    }

    const { data, error } = await supabase
      .from("catalog_import_rows")
      .select("row_number, status, action, matched_item_id, candidate_item_ids, result_message, error_message, normalized_data")
      .eq("job_id", jobId)
      .in("status", ["review", "error"])
      .order("row_number", { ascending: true })
      .limit(100);

    if (error) {
      console.error("Unable to load import review rows:", error);
      return;
    }

    setReviewRows(data || []);
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const shouldPoll = activeJob && ACTIVE_STATUSES.has(activeJob.status);
    if (!shouldPoll) return undefined;

    const timer = window.setInterval(loadHistory, POLL_MS);
    return () => window.clearInterval(timer);
  }, [activeJob, loadHistory]);

  useEffect(() => {
    if (activeJob?.status === "needs_review" || activeJob?.status === "failed") {
      loadReviewRows(activeJob.id);
    } else {
      setReviewRows([]);
    }
  }, [activeJob?.id, activeJob?.status, loadReviewRows]);

  const chooseFile = () => {
    if (!submitting) fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setFileName(file.name);
    setErrorMessage("");
    setStageProgress(0);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const releaseSheet = findSheetName(workbook, PACKAGE_SHEETS.release);
      const familySheet = findSheetName(workbook, PACKAGE_SHEETS.family);
      const releasesSheet = findSheetName(workbook, PACKAGE_SHEETS.releases);
      const canonicalSheet = findSheetName(workbook, PACKAGE_SHEETS.canonical);
      const isFamilyPackage = Boolean(familySheet && releasesSheet && canonicalSheet);
      const isPackage = Boolean((releaseSheet && canonicalSheet) || isFamilyPackage);

      let rawRows = [];

      if (isFamilyPackage) {
        const parsedFamily = await resolveReleaseTaxonomy(parseFamilySheet(workbook, familySheet));
        const releases = parseReleasesSheet(workbook, releasesSheet);
        if (!parsedFamily.brand || parsedFamily.year === null || !parsedFamily.category_id) {
          throw new Error("Family sheet must resolve a brand, year, and category.");
        }
        if (!parsedFamily.is_multi_sport && !parsedFamily.subcategory_id) {
          throw new Error("Family sheet must resolve a subcategory unless explicitly multi-sport.");
        }
        if (!parsedFamily.family_key || !parsedFamily.family_name) {
          throw new Error("Family sheet must include family_key and family_name.");
        }
        if (!releases.length || releases.some((r) => !r.release_key || !r.release_name || !r.display_release_name_supplied)) {
          throw new Error("Releases sheet requires release_key, release_name, and explicit display_release_name for every row.");
        }
        const keys = releases.map((r) => normalizeText(r.release_key));
        if (new Set(keys).size !== keys.length) throw new Error("Releases sheet contains duplicate release_key values.");

        rawRows = sheetRows(workbook, canonicalSheet);
        if (!rawRows.length) throw new Error("No rows were found in the Canonical Items worksheet.");
        const validKeys = new Set(keys);
        const missingReleaseRows = rawRows.filter((row) => !validKeys.has(normalizeText(row.release_key)));
        if (missingReleaseRows.length) {
          throw new Error(`${missingReleaseRows.length} Canonical Items row(s) have a missing or unknown release_key.`);
        }

        validateCanonicalIdentity(rawRows, releases, { strict: Boolean(parsedFamily.product_name) });

        const variants = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.variants));
        const applicability = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.applicability));
        const qa = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.qa));
        const sources = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.sources));
        const familyCheck = await preflightPackageFamily(parsedFamily);
        const releaseChecks = [];
        for (const release of releases) {
          releaseChecks.push({ release_key: release.release_key, ...(await preflightPackageRelease({ ...parsedFamily, ...release }, familyCheck)) });
        }
        // Multi-release preflight must validate every release independently.
        // A "match" without a concrete ID is not safe to queue.
        const checkedReleases = releaseChecks.map((r) => ({
          ...r,
          status: r.status === "match" && !r.release_id ? "review" : r.status,
          message: r.status === "match" && !r.release_id
            ? "Release reported a match without a release_id. Verify the release resolver response."
            : r.message,
        }));
        const aggregateReleaseCheck = {
          status: checkedReleases.some((r) => r.status === "review" || !["match", "create"].includes(r.status))
            ? "review"
            : checkedReleases.every((r) => r.status === "match") ? "match" : "create",
          releases: checkedReleases,
        };

        setImportMode("package");
        setFamilyPreflight(familyCheck);
        setReleasePreflight(aggregateReleaseCheck);
        setPackageData({ version: 3, family: parsedFamily, release: { ...parsedFamily, ...releases[0] }, releases, variants, applicability, qa, sources });
      } else if (isPackage) {
        const parsedRelease = await resolveReleaseTaxonomy(parseReleaseSheet(workbook, releaseSheet));
        if (!parsedRelease.brand || parsedRelease.year === null || !parsedRelease.category_id) throw new Error("Package Release sheet must resolve a brand, year, and category.");
        if (!parsedRelease.is_multi_sport && !parsedRelease.subcategory_id) throw new Error("Package Release sheet must resolve a subcategory unless it is explicitly multi-sport.");
        if (!parsedRelease.family_key || !parsedRelease.family_name || !parsedRelease.release_name) throw new Error("Package Release sheet must include family_key, family_name, and release_name.");
        if (!parsedRelease.display_release_name_supplied) throw new Error("Package Release sheet must explicitly include display_release_name (true/false).");
        rawRows = sheetRows(workbook, canonicalSheet);
        if (!rawRows.length) throw new Error("No rows were found in the Canonical Items worksheet.");
        if (parsedRelease.set_name) validateCanonicalIdentity(rawRows.map((row) => ({ ...row, release_key: parsedRelease.release_key || "default" })), [{ release_key: parsedRelease.release_key || "default", set_name: parsedRelease.set_name }]);
        const variants = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.variants));
        const applicability = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.applicability));
        const qa = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.qa));
        const sources = sheetRows(workbook, findSheetName(workbook, PACKAGE_SHEETS.sources));
        const familyCheck = await preflightPackageFamily(parsedRelease);
        const releaseCheck = await preflightPackageRelease(parsedRelease, familyCheck);
        const safeReleaseCheck = releaseCheck?.status === "match" && !releaseCheck.release_id
          ? { ...releaseCheck, status: "review", message: "Release reported a match without a release_id." }
          : releaseCheck;
        setImportMode("package"); setFamilyPreflight(familyCheck); setReleasePreflight(safeReleaseCheck);
        setPackageData({ version: 2, release: parsedRelease, variants, applicability, qa, sources });
      } else {
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        rawRows = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: "" });
        if (!rawRows.length) throw new Error("No data was found in the first worksheet.");
        setImportMode("canonical"); setFamilyPreflight(null); setReleasePreflight(null); setPackageData(null);
      }

      const normalized = rawRows.map(normalizeRow);
      const taxonomyResolved = await resolveTaxonomyForRows(normalized);
      setRows(taxonomyResolved);
    } catch (error) {
      console.error("Unable to read import file:", error);
      setRows([]);
      setPackageData(null);
      setFamilyPreflight(null);
      setReleasePreflight(null);
      setImportMode("canonical");
      setErrorMessage(error.message || "Unable to read that Excel file.");
    }
  };

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    // V3.1: keep template sheets and importer documentation together.
    {
      const rows = [
      {
            "Section": "VERSION",
            "Rule": "Hobbli Catalog Import V4",
            "Details": "V3.1 extends V3 family packages with authoritative card-number variant applicability. Upload this workbook in a new ChatGPT conversation to restore the import rules quickly."
      },
      {
            "Section": "PACKAGE",
            "Rule": "Required sheets",
            "Details": "Family, Releases, Canonical Items, Variants, Variant Applicability, QA, Sources. README is documentation only and is ignored by the importer."
      },
      {
            "Section": "MODELING",
            "Rule": "Family / release structure",
            "Details": "Model the product family once, then use Releases for Base, series, inserts, autograph/relic checklists, and other distinct canonical checklists. Check for an existing family/release before creating duplicates. Supply explicit Family.product_name for V4 resolution; legacy packages without it continue using V3."
      },
      {
            "Section": "MODELING",
            "Rule": "Canonical cards",
            "Details": "Canonical Items represent card identities, not ordinary parallels. Card numbers are identity strings and must remain text (examples: 001, US1, BCP-1, APC-MN)."
      },
      {
            "Section": "MODELING",
            "Rule": "Completeness",
            "Details": "If Hobbli undertakes a product, capture the complete reliably documented product: base, inserts, autograph/relic subsets, variations/short prints as appropriate, and verified parallel applicability. Do not knowingly leave isolated documented cards out of a completed product."
      },
      {
            "Section": "SOURCES",
            "Rule": "Source hierarchy",
            "Details": "Prefer official manufacturer checklists/PDFs. Use reliable secondary sources when needed. Never silently invent or infer unsupported checklist rows or applicability. Record sources on the Sources sheet."
      },
      {
            "Section": "DATA",
            "Rule": "Category convention",
            "Details": "Use exactly 'Sports Card' for sports cards. Do not use 'SportsCard'."
      },
      {
            "Section": "DATA",
            "Rule": "Years",
            "Details": "Use year for the product start year. Leave year_end blank unless the product genuinely spans years. year_end may not be earlier than year."
      },
      {
            "Section": "DATA",
            "Rule": "Release routing",
            "Details": "Every Canonical Items.release_key must match exactly one Releases.release_key. Releases.release_key values must be unique within the package."
      },
      {
            "Section": "DATA",
            "Rule": "display_release_name",
            "Details": "Every Releases row must explicitly provide true or false. Use false when the family name alone should identify the checklist; use true when the release/checklist name is meaningful to collectors."
      },
      {
            "Section": "DATA",
            "Rule": "Multi-sport",
            "Details": "Set is_multi_sport=true only when a family genuinely spans sports/categories such that one sports subcategory would be misleading. Otherwise provide the correct subcategory."
      },
      {
            "Section": "VARIANTS",
            "Rule": "Global definitions",
            "Details": "Variants are global definitions and are reused across products when the normalized variant is the same. Applicability is release-specific."
      },
      {
            "Section": "VARIANTS",
            "Rule": "Parallel vs canonical",
            "Details": "Ordinary parallels/variations normally belong in Variants + Variant Applicability rather than duplicate Canonical Items. Create a distinct canonical checklist/release when the product documents a genuinely separate checklist identity."
      },
      {
            "Section": "VARIANTS",
            "Rule": "Serial numbering",
            "Details": "Store the canonical print-run denominator in variants.attributes.serial_total. A collector card's serial numerator belongs on user_items.attributes.serial_number, not on the canonical variant."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "release scope",
            "Details": "scope_type=release + mode=include means the variant is available for every canonical card in that release."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "subset scope",
            "Details": "scope_type=subset + mode=include requires subset_name and applies to the complete matching canonical subset."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "item scope",
            "Details": "Existing item-level mappings may use card_number/item_name where a single canonical card is being mapped. The finalizer requires an unambiguous canonical match."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "V3.1 card_numbers scope",
            "Details": "scope_type=card_numbers + mode=include is an authoritative complete selection for one release+variant. Put all target card numbers in ONE row, separated by | (pipe). Comma, semicolon, and line-break delimiters are also accepted."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "Authoritative selection",
            "Details": "card_numbers must represent the COMPLETE verified selection for that release+variant, not an incremental fragment. Every selector must resolve to exactly one canonical card or finalization fails without guessing."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "Normalization",
            "Details": "The database normalizes authoritative selections to the smallest safe rule graph: release, subset, item/group INCLUDE, or release INCLUDE plus item/group EXCLUDE. Do not pre-optimize the workbook list."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "One selector row",
            "Details": "Use one card_numbers applicability row per release_key + variant_key authoritative selection. Do not split one selection across multiple rows because each authoritative application replaces the previous selection."
      },
      {
            "Section": "APPLICABILITY",
            "Rule": "No guessing",
            "Details": "If a manufacturer names a parallel but does not document which subjects receive it, define the variant if appropriate but leave applicability unapplied until verified."
      },
      {
            "Section": "QA",
            "Rule": "Canonical identity checks",
            "Details": "Before import, check duplicate canonical identities, unexpected missing card numbers, release-key integrity, checklist counts, and source reconciliation."
      },
      {
            "Section": "QA",
            "Rule": "Applicability checks",
            "Details": "For card_numbers mappings, reconcile the selector count to the source list and verify that all selectors belong to the target release. Missing or ambiguous selectors are blocking errors."
      },
      {"Section": "VERSION", "Rule": "Specification", "Details": "Hobbli Catalog Import V4 canonical identity and V3.1 variant applicability. This README is the standing contract for future checklist creation; read it before building packages."},
      {"Section": "WORKFLOW", "Rule": "Existing data first", "Details": "Before creating a package, audit Hobbli for existing family, releases, canonical items, variants, and applicability. Reuse or repair existing identities; never create duplicate families or releases."},
      {"Section": "WORKFLOW", "Rule": "Full product coverage", "Details": "When choosing a product, model the complete reliably documented product: base, inserts, autographs, relics, variations, short prints, and verified parallel applicability. Do not stop at only large subsets."},
      {"Section": "WORKFLOW", "Rule": "Source evidence", "Details": "Prefer manufacturer checklists, then reputable complete checklist sources. Record URLs and provenance in Sources; do not invent cards, names, serial counts, or variant applicability."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Read this first", "Details": "In a new ChatGPT conversation, read this entire README before researching or building Hobbli checklist packages. The default is batch-first, complete-product delivery with minimal unnecessary back-and-forth."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Audit first", "Details": "Before creating a workbook, audit existing Hobbli families, releases, canonical items, variants and applicability. Reuse or safely complete what exists; avoid duplicate imports. Ask for one consolidated database audit only when necessary."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "One research pass", "Details": "Once a product is selected, research the full reliably documented product in one coordinated pass: base, inserts, autograph/relic checklists, variations/short prints and verified parallel applicability. Do not stop after each small subset."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Provide direct source links early", "Details": "Proactively provide direct navigable links to official manufacturer checklists/PDFs, TCDB PDF checklists or other trustworthy complete sources when needed. Invite the user to upload documents in one batch; do not repeatedly ask permission to look for sources."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Largest safe package", "Details": "Build the largest practical complete V4 workbook in one delivery, including all verified releases and variants/applicability. Prefer one product-level package over separate micro-packages for individual inserts; split only for genuine size or source constraints."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Avoid micro-steps", "Details": "Do not repeatedly ask whether to proceed to the next insert, subset or checklist when the user has already approved completing the product. Work through the remaining documented material and deliver the package. Ask only for a material decision or missing essential source."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Batch dependencies", "Details": "When multiple documents or audits are needed, request them together up front. If a product must be split, organize substantial logical batches and clearly identify what each batch covers and what remains."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Accuracy over speed", "Details": "Never invent checklist rows, player names, card numbers, variants, serial totals or applicability. Distinguish documented facts from unverified gaps; defer only the unsupported portion rather than silently fabricating it."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Deliver import-ready", "Details": "Deliver a queue-ready V4 workbook with Family, Releases, Canonical Items, Variants, Variant Applicability, QA and Sources as applicable. Check set_name/subset declarations per release, release-key routing, duplicate identity, source counts and existing Hobbli records."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "No automatic inventory links", "Details": "Never automatically link user_items to canonical items. Item Review Manager approval remains mandatory even when canonical cards are imported or repaired."},
      {"Section": "FUTURE CHAT WORKFLOW", "Rule": "Default instruction", "Details": "When the user selects a product, proceed with comprehensive research and assemble as much verified import-ready material as possible without unnecessary stop-and-confirm cycles. Provide source links when useful and ask only for essential inputs."},
      {"Section": "IDENTITY", "Rule": "Three separate concepts", "Details": "Family identifies the product; Releases divide checklist groups internally; Canonical Items.set_name is the collector-facing set identity. Never blindly copy release_name into item set_name."},
      {"Section": "IDENTITY", "Rule": "Release mapping required", "Details": "For V4 packages with Family.product_name, each Releases row MUST contain release_key, release_name, display_release_name, set_name, and subset columns. A blank subset cell explicitly means no subset."},
      {"Section": "IDENTITY", "Rule": "Canonical row agreement", "Details": "Every Canonical Items row must use the same set_name and subset as its declared Releases row. release_key routes to the release; it is not a substitute for the collector-facing identity."},
      {"Section": "IDENTITY", "Rule": "Set naming", "Details": "Use the actual collector-facing set name, e.g. 2025 Topps Chrome Baseball cards use set_name Chrome; Base Cards is an internal release label, not the canonical set."},
      {"Section": "IDENTITY", "Rule": "Subset meaning", "Details": "Use subset for a genuine named checklist grouping such as Future Stars or All Etch. Base cards often have no subset. Do not invent subsets for historical sets or force a non-null subset."},
      {"Section": "IDENTITY", "Rule": "Historical sets", "Details": "Older cards (e.g. T206, T205, 1950s Topps) can legitimately have NULL/blank subset throughout. A single release named like the set is not automatically suspicious."},
      {"Section": "IDENTITY", "Rule": "Exceptions", "Details": "Some releases need distinct collector-facing set names or special identity modeling. Explicitly research and declare each release mapping rather than assuming one set for the whole family."},
      {"Section": "IDENTITY", "Rule": "Paper and Chrome", "Details": "Paper versus Chrome may be identity-defining and may require distinct canonical groupings. Model from documented checklists, not a universal auto-rule."},
      {"Section": "IDENTITY", "Rule": "Parallels versus subsets", "Details": "Ordinary color, refractor, serial-numbered and similar parallels belong in Variants plus Variant Applicability, not duplicate canonical items. Distinct documented checklists may justify separate canonical rows."},
      {"Section": "IDENTITY", "Rule": "Variation identity", "Details": "When a genuinely distinct canonical card shares the same player and number, use a verified variation discriminator in additional_details.variation; do not create arbitrary duplicates."},
      {"Section": "IDENTITY", "Rule": "Card number strings", "Details": "Card numbers are strings. Preserve leading zeros, letters, hyphens and prefixes exactly: 001, US1, BCP-1, APC-MN."},
      {"Section": "IDENTITY", "Rule": "Year ranges", "Details": "year is the issue start year. year_end remains blank unless genuinely multi-year; year_end must not precede year."},
      {"Section": "PACKAGE", "Rule": "Required sheets", "Details": "Family, Releases, Canonical Items, Variants, Variant Applicability, QA, Sources. README is documentation and is ignored by the importer."},
      {"Section": "PACKAGE", "Rule": "Release keys", "Details": "Every Canonical Items.release_key must resolve to exactly one unique Releases.release_key. display_release_name must be explicitly true or false."},
      {"Section": "PACKAGE", "Rule": "Taxonomy", "Details": "Use category Sports Card for sports cards. Select the correct subcategory; use is_multi_sport only when genuinely applicable."},
      {"Section": "VARIANTS", "Rule": "Global reuse", "Details": "Variant definitions are global; reuse existing matching variants. Apply variants only to verified releases, subsets, card numbers, or items."},
      {"Section": "VARIANTS", "Rule": "Serial numbers", "Details": "Store print-run denominator in variants.attributes.serial_total. The unique serial numerator belongs on user_items.attributes.serial_number."},
      {"Section": "VARIANTS", "Rule": "Authoritative selectors", "Details": "For scope_type=card_numbers, list the COMPLETE verified card-number selection for one release_key + variant_key in ONE row, pipe-separated. Do not split across multiple rows."},
      {"Section": "VARIANTS", "Rule": "Unverified applicability", "Details": "If sources do not establish which subjects have a parallel, do not guess. Define the variant if appropriate but leave applicability unapplied until verified."},
      {"Section": "QA", "Rule": "Blocking checks", "Details": "Check missing names, invalid years/taxonomy, duplicate identities, release-key integrity, declared canonical set/subset consistency, checklist counts, and source reconciliation before queueing."},
      {"Section": "QA", "Rule": "Accuracy beyond consistency", "Details": "A workbook can be internally consistent but factually wrong. Validate collector-facing set/subset labels against documented sources and known existing Hobbli records."},
      {"Section": "QA", "Rule": "Existing links", "Details": "Never automatically link user_items to canonical items during checklist import or repair. Item Review Manager must retain manual approval."},
      {"Section": "QA", "Rule": "Repair safety", "Details": "For existing families, inspect release-to-set/subset mappings first; repair in place only after explicit review. Preserve item IDs, release IDs, variant rules, and user-item relationships. Never blanket-update historical sets."},
      {"Section": "QA", "Rule": "Importer defense", "Details": "Frontend validates declared identity before staging. Database-side staged-job validation is also required for complete protection; frontend-only validation is not a security boundary."},
      {"Section": "HANDOFF", "Rule": "Future assistant instruction", "Details": "When using this workbook as reference in a later chat: read all README rules; audit existing Hobbli data; verify authoritative checklist sources; produce complete queue-ready package; validate set/subset per release and variants; do not invent data."},
      {
            "Section": "VERSION HISTORY",
            "Rule": "V3.0",
            "Details": "Family/release package architecture with global variant definitions and declarative release/subset/item applicability."
      },
      {
            "Section": "VERSION HISTORY",
            "Rule": "V3.1",
            "Details": "Adds authoritative card-number selector applicability and embeds this Hobbli import specification in every template/package."
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["Section", "Rule", "Details"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "README - Hobbli V4");
    }
    {
      const rows = [
      {
            "field": "family_key",
            "value": "2026_example_football"
      },
      {
            "field": "family_name",
            "value": "Example Football"
      },
      {
            "field": "product_name",
            "value": "Example Product"
      },
      {
            "field": "manufacturer",
            "value": "Example Manufacturer"
      },
      {
            "field": "brand",
            "value": "Example Brand"
      },
      {
            "field": "year",
            "value": 2026
      },
      {
            "field": "year_end",
            "value": ""
      },
      {
            "field": "category_name",
            "value": "Sports Card"
      },
      {
            "field": "subcategory_name",
            "value": "Football"
      },
      {
            "field": "is_multi_sport",
            "value": false
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["field", "value"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Family");
    }
    {
      const rows = [
      {
            "release_key": "base",
            "release_name": "Base",
            "display_release_name": false,
            "set_name": "Example Product",
            "subset": ""
      },
      {
            "release_key": "insert_example",
            "release_name": "Insert Example",
            "display_release_name": true,
            "set_name": "Example Product",
            "subset": "Insert Example"
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["release_key", "release_name", "display_release_name", "set_name", "subset"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 17}, {"wch": 25}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Releases");
    }
    {
      const rows = [
      {
            "release_key": "base",
            "name": "Example Base Player",
            "brand": "Example Brand",
            "set_name": "Example Product",
            "subset": "",
            "year": 2026,
            "year_end": "",
            "category_name": "Sports Card",
            "subcategory_name": "Football",
            "card_number": "1",
            "team": "Example Team",
            "variation": ""
      },
      {
            "release_key": "insert_example",
            "name": "Example Insert Player",
            "brand": "Example Brand",
            "set_name": "Example Product",
            "subset": "Insert Example",
            "year": 2026,
            "year_end": "",
            "category_name": "Sports Card",
            "subcategory_name": "Football",
            "card_number": "I-1",
            "team": "Example Team",
            "variation": ""
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["release_key", "name", "brand", "set_name", "subset", "year", "year_end", "category_name", "subcategory_name", "card_number", "team", "variation"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 18}, {"wch": 21}, {"wch": 16}, {"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Canonical Items");
    }
    {
      const rows = [
      {
            "variant_key": "example_parallel",
            "name": "Example Parallel",
            "category": "Parallel",
            "description": "Example only; delete this row when not needed.",
            "attributes": ""
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["variant_key", "name", "category", "description", "attributes"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Variants");
    }
    {
      const rows = [
      {
            "release_key": "base",
            "variant_key": "example_parallel",
            "scope_type": "release",
            "mode": "include",
            "subset_name": "",
            "card_numbers": "",
            "card_number": "",
            "item_name": "",
            "source_note": "Example release-wide applicability; delete or replace."
      },
      {
            "release_key": "base",
            "variant_key": "example_parallel",
            "scope_type": "subset",
            "mode": "include",
            "subset_name": "Example Subset",
            "card_numbers": "",
            "card_number": "",
            "item_name": "",
            "source_note": "Example subset applicability; delete or replace."
      },
      {
            "release_key": "base",
            "variant_key": "example_parallel",
            "scope_type": "card_numbers",
            "mode": "include",
            "subset_name": "",
            "card_numbers": "1|3|7|12",
            "card_number": "",
            "item_name": "",
            "source_note": "V3.1 example: complete authoritative card-number selection; delete or replace."
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["release_key", "variant_key", "scope_type", "mode", "subset_name", "card_numbers", "card_number", "item_name", "source_note"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 16}, {"wch": 17}, {"wch": 16}, {"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Variant Applicability");
    }
    {
      const rows = [
      {
            "check": "V3.1 package routing",
            "expected": "Each Canonical Items release_key matches exactly one Releases.release_key.",
            "notes": "Delete example rows and replace with verified package QA notes."
      },
      {
            "check": "V3.1 card_numbers",
            "expected": "Each authoritative selector resolves uniquely in its target release.",
            "notes": "Reconcile selector counts to the source checklist."
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["check", "expected", "notes"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "QA");
    }
    {
      const rows = [
      {
            "source": "Example source",
            "url": "",
            "notes": "Record the checklist/release source used to verify this package."
      }
];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: ["source", "url", "notes"] });
      worksheet["!cols"] = [{"wch": 16}, {"wch": 16}, {"wch": 16}];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sources");
    }
    XLSX.writeFile(workbook, "hobbli-v4-canonical-identity-template.xlsx");
  };

  const submitQueuedImport = async () => {
    if (!rows.length || submitting || hasBlockingPreflightIssues) return;
    if (packageData?.version === 3) {
      try {
        validateCanonicalIdentity(rows, packageData.releases, { strict: Boolean(packageData.family?.product_name) });
      } catch (validationError) {
        setErrorMessage(validationError.message);
        return;
      }
    }

    const packageLabel = importMode === "package"
      ? ` as a package for ${packageData?.release?.family_name || "the resolved family"} / ${packageData?.release?.release_name || "the resolved release"}`
      : "";
    const confirmed = window.confirm(
      `Queue ${rows.length} row${rows.length === 1 ? "" : "s"}${packageLabel} for server-side canonical reconciliation?\n\n` +
        "Production rules: exact matches are enriched; exactly one safe NULL-card shell may be repaired; ambiguous matches are sent to review; otherwise a new canonical is inserted.\n\n" +
        (importMode === "package"
          ? "This package imports canonical cards, resolves/creates variant definitions, and applies verified item-level variant mappings after canonical reconciliation succeeds. Pending applicability rows remain unapplied.\n\n"
          : "") +
        "After the upload reaches 100% and the job is queued, you can leave this page or close Hobbli.",
    );

    if (!confirmed) return;

    setSubmitting(true);
    setStageProgress(0);
    setErrorMessage("");

    let jobId = null;

    try {
      let createdJobId = null;
      let createError = null;

      if (importMode === "package" && packageData?.version === 3 && packageData?.family?.product_name) {
        const family = packageData.family;
        const manifest = {
          sheets: { canonical_items: rows.length, variants: packageData.variants.length,
            variant_applicability: packageData.applicability.length,
            qa: packageData.qa.length, sources: packageData.sources.length },
          variants: packageData.variants, variant_applicability: packageData.applicability,
          component_policy: { import_variant_definitions: true,
            apply_only_verified_applicability: true, pending_applicability_is_non_blocking: true },
        };
        const { data, error } = await supabase.rpc("catalog_import_initialize_v4_package", {
          p_filename: fileName || "catalog-package.xlsx", p_total_rows: rows.length,
          p_family: family, p_releases: packageData.releases,
          p_manifest: manifest,
          p_preflight: { family: familyPreflight || {}, release: releasePreflight || {} },
        });
        if (error) throw error;
        if (!data?.job_id || !data?.release_map) throw new Error("V4 initialization returned no job or release map.");
        createdJobId = data.job_id;
        const releaseMap = data.release_map;
        rows.forEach((row) => {
          const resolvedId = releaseMap[normalizeText(row.release_key)];
          if (!resolvedId) throw new Error(`Unknown release_key ${row.release_key} while staging.`);
          row.release_id = Number(resolvedId);
        });
      } else if (importMode === "package") {
        const isV3 = packageData?.version === 3;
        const family = isV3 ? packageData?.family : packageData?.release;
        if (!family) throw new Error("Package family metadata is missing.");

        const { data: resolvedFamily, error: familyResolveError } = await supabase.rpc(
          family.product_name ? "catalog_import_resolve_family_v4" : "catalog_import_resolve_family",
          {
            p_category_id: family.category_id, p_subcategory_id: family.is_multi_sport ? null : family.subcategory_id,
            p_year: family.year, p_year_end: family.year_end, p_brand: family.brand, p_family_name: family.family_name,
            ...(family.product_name ? { p_product_name: family.product_name } : {}),
            p_family_key: family.family_key || null, p_is_multi_sport: Boolean(family.is_multi_sport), p_allow_create: true,
          },
        );
        if (familyResolveError) throw familyResolveError;
        if (!resolvedFamily?.success || !resolvedFamily?.family_id) throw new Error(resolvedFamily?.message || "Release family could not be resolved safely.");

        const releasesToResolve = isV3 ? packageData.releases : [packageData.release];
        const resolvedReleases = [];
        for (const release of releasesToResolve) {
          const { data: resolvedRelease, error: resolveError } = await supabase.rpc("catalog_import_resolve_family_release", {
            p_release_family_id: resolvedFamily.family_id, p_release_name: release.release_name,
            p_display_release_name: Boolean(release.display_release_name), p_allow_create: true,
          });
          if (resolveError) throw resolveError;
          if (!resolvedRelease?.success || !resolvedRelease?.release_id) throw new Error(resolvedRelease?.message || `Release ${release.release_name} could not be resolved safely.`);
          resolvedReleases.push({ ...release, release_id: resolvedRelease.release_id });
        }
        const releaseMap = Object.fromEntries(resolvedReleases.map((r) => [normalizeText(r.release_key || packageData.release?.release_key || "default"), r.release_id]));
        const primaryRelease = resolvedReleases[0];
        const manifest = {
          version: isV3 ? 3 : 2,
          schema_version: isV3 ? "3.1" : "2",
          family_key: family.family_key, family_name: family.family_name,
          ...(family.product_name ? { product_name: family.product_name } : {}),
          release_key: isV3 ? null : primaryRelease.release_key || null, release_name: isV3 ? null : primaryRelease.release_name,
          releases: resolvedReleases, release_map: releaseMap,
          sheets: { canonical_items: rows.length, variants: packageData?.variants?.length || 0, variant_applicability: packageData?.applicability?.length || 0, qa: packageData?.qa?.length || 0, sources: packageData?.sources?.length || 0 },
          variants: packageData?.variants || [], variant_applicability: packageData?.applicability || [],
          component_policy: { import_variant_definitions: true, apply_only_verified_applicability: true, pending_applicability_is_non_blocking: true },
        };
        const created = await supabase.rpc("catalog_import_create_package_job", {
          p_filename: fileName || "catalog-package.xlsx", p_total_rows: rows.length, p_release_id: primaryRelease.release_id,
          p_package_manifest: manifest, p_package_preflight: { family: familyPreflight || {}, release: releasePreflight || {} },
          p_release_family_id: resolvedFamily.family_id,
        });
        createdJobId = created.data; createError = created.error;

        if (!createError && isV3) {
          rows.forEach((row) => {
            const resolvedId = releaseMap[normalizeText(row.release_key)];
            if (!resolvedId) throw new Error(`Unknown release_key ${row.release_key} while staging.`);
            row.release_id = resolvedId;
          });
        }
      } else {
        const created = await supabase.rpc("catalog_import_create_job", {
          p_filename: fileName || "catalog-import.xlsx",
          p_total_rows: rows.length,
        });
        createdJobId = created.data;
        createError = created.error;
      }

      if (createError) throw createError;
      jobId = createdJobId;

      const stagedPayload = rows.map((row, index) => ({
        row_number: index + 2,
        normalized_data: stripTaxonomyNames(row),
      }));

      const chunks = chunkArray(stagedPayload, STAGE_CHUNK_SIZE);
      let stagedCount = 0;

      for (const chunk of chunks) {
        const { data: totalStaged, error: stageError } = await supabase.rpc(
          "catalog_import_stage_rows",
          {
            p_job_id: jobId,
            p_rows: chunk,
          },
        );

        if (stageError) throw stageError;
        stagedCount = Number(totalStaged || stagedCount + chunk.length);
        setStageProgress(Math.min(100, Math.round((stagedCount / rows.length) * 100)));
      }

      // V4 server-side verification: validate every staged canonical identity before enqueue.
      // This checks the database's actual staged JSON, not only the workbook preview.
      if (importMode === "package" && packageData?.version === 3 && packageData?.family?.product_name) {
        const declarations = packageData.releases.map((release) => {
          const example = rows.find((row) => normalizeText(row.release_key) === normalizeText(release.release_key));
          if (!example?.release_id) throw new Error(`No resolved release_id for ${release.release_key}`);
          return { release_id: example.release_id, set_name: release.set_name, subset: release.subset ?? "" };
        });
        const { data: identityResult, error: identityError } = await supabase.rpc(
          "catalog_import_verify_v4_staged_identity",
          { p_job_id: jobId, p_declarations: declarations },
        );
        if (identityError) throw identityError;
        if (!identityResult?.valid || Number(identityResult.staged_rows) !== rows.length) {
          throw new Error("Server canonical identity verification did not confirm all staged rows.");
        }
      }

      const { data: queuedJob, error: enqueueError } = await supabase.rpc(
        "catalog_import_enqueue_job",
        { p_job_id: jobId },
      );

      if (enqueueError) throw enqueueError;

      setActiveJob(Array.isArray(queuedJob) ? queuedJob[0] : queuedJob);
      setRows([]);
      setFileName("");
      setPackageData(null);
      setFamilyPreflight(null);
      setReleasePreflight(null);
      setImportMode("canonical");
      setStageProgress(100);
      await loadHistory();
    } catch (error) {
      console.error("Unable to queue catalog import:", error);
      const readable = getSupabaseErrorMessage(
        error,
        error?.message || "Unable to queue the catalog import.",
      );
      setErrorMessage(readable);

      if (String(readable).toLowerCase().includes("catalog import access")) {
        setErrorMessage(
          `${readable}. Run 02_authorize_catalog_import_admin.sql with your Supabase Auth user UUID.`,
        );
      }

      // A failed staging upload intentionally leaves the job in "staging".
      // Because staging RPCs are idempotent, a future resumable UI can continue it.
      // For now, history makes the incomplete job visible instead of hiding it.
      if (jobId) await loadHistory();
    } finally {
      setSubmitting(false);
    }
  };

  const cancelJob = async (jobId) => {
    if (!jobId) return;
    if (!window.confirm("Cancel this import job? Rows already committed to items will not be rolled back.")) {
      return;
    }

    const { error } = await supabase.rpc("catalog_import_cancel_job", {
      p_job_id: jobId,
    });

    if (error) {
      setErrorMessage(getSupabaseErrorMessage(error, error.message || "Unable to cancel job."));
      return;
    }

    await loadHistory();
  };

  const selectHistoryJob = async (job) => {
    setActiveJob(job);
    setErrorMessage("");
    if (job.status === "needs_review" || job.status === "failed") {
      await loadReviewRows(job.id);
    }
  };

  const progress = jobProgress(activeJob);

  return (
    <div>
      <section className="hadmin-panel">
        <div className="hadmin-section-heading">
          <div>
            <h2>Canonical Catalog Import</h2>
            <p>
              Production V3 accepts ordinary canonical workbooks and release packages. Once canonical rows are staged and queued,
              the database worker continues processing even if this page is closed.
            </p>
          </div>
        </div>

        <div className="hadmin-info">
          <strong>Conservative reconciliation:</strong> exact canonical identities are enriched;
          one unique matching NULL-card shell may be repaired; ambiguous candidates are sent to
          review; otherwise a new canonical item is inserted. Card numbers are always treated as text.
        </div>

        <div className="hadmin-toolbar">
          <button type="button" className="hadmin-btn" onClick={chooseFile} disabled={submitting}>
            Choose Excel File
          </button>
          <button
            type="button"
            className="hadmin-btn-secondary"
            onClick={downloadTemplate}
            disabled={submitting}
          >
            Download Template
          </button>

          {fileName && <span className="hadmin-pill blue">{fileName}</span>}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {errorMessage && <div className="hadmin-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        {rows.length > 0 && (
          <>
            {importMode === "package" && packageData && (
              <div className="hadmin-card" style={{ marginTop: 16 }}>
                <div className="hadmin-row-between">
                  <div>
                    <strong>Release Package</strong>
                    <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {(packageData.family || packageData.release).family_name || "Unnamed family"} → {packageData.version === 3 ? `${packageData.releases.length} releases` : (packageData.release.release_name || "Unnamed release")}
                      {(packageData.family || packageData.release).family_key ? ` · ${(packageData.family || packageData.release).family_key}` : ""}
                    </div>
                  </div>
                  <span className={`hadmin-pill ${familyPreflight?.status === "review" || releasePreflight?.status === "review" ? "red" : "blue"}`}>
                    {familyPreflight?.status === "review"
                      ? "Family review required"
                      : releasePreflight?.status === "review"
                        ? "Release review required"
                        : familyPreflight?.status === "match" && releasePreflight?.status === "match"
                          ? packageData.version === 3
                            ? `Reuse family #${familyPreflight.family_id} / ${releasePreflight.releases.length} existing releases`
                            : `Reuse family #${familyPreflight.family_id} / release #${releasePreflight.release_id}`
                          : familyPreflight?.status === "create"
                            ? "Create family + release"
                            : releasePreflight?.status === "create"
                              ? `Reuse family #${familyPreflight?.family_id} / create release`
                              : "Checking family / release"}
                  </span>
                </div>

                {packageData.version === 3 && Array.isArray(releasePreflight?.releases) && (
                  <div style={{ marginTop: 12 }}>
                    <strong style={{ fontSize: 12 }}>Release-by-release preflight</strong>
                    <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
                      {releasePreflight.releases.map((result) => {
                        const release = packageData.releases.find((r) => r.release_key === result.release_key);
                        const matched = result.status === "match" && Boolean(result.release_id);
                        return (
                          <div key={result.release_key} className="hadmin-muted" style={{ fontSize: 12 }}>
                            <strong>{release?.release_name || result.release_key}</strong>
                            {` (${result.release_key}) — `}
                            {matched ? `Reuse release #${result.release_id}`
                              : result.status === "create" ? "Would create release"
                              : `Review required${result.message ? `: ${result.message}` : ""}`}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 10 }}>
                  {packageData.release.year}{packageData.release.year_end && packageData.release.year_end !== packageData.release.year ? `–${packageData.release.year_end}` : ""}
                  {` · ${packageData.release.brand || "—"} · ${packageData.release.family_name || "—"} / ${packageData.release.release_name || "—"}`}
                  {` · ${packageData.release.category_name || "—"} / ${packageData.release.subcategory_name || (packageData.release.is_multi_sport ? "Multi-sport" : "—")}`}
                </div>

                <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  Package contents: {rows.length} canonical rows · {packageData.variants.length} variants · {packageData.applicability.length} applicability rows · {packageData.qa.length} QA rows · {packageData.sources.length} sources.
                  Variant definitions will be resolved or created after canonical reconciliation succeeds. Verified applicability mappings will then be applied; pending mappings remain unapplied.
                </div>
              </div>
            )}

            <div className="hadmin-card" style={{ marginTop: 16 }}>
              <div className="hadmin-row-between">
                <div>
                  <strong>{preflight.total} rows ready</strong>
                  <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {preflight.numericCardNumbers} numeric card numbers · {preflight.alphanumericCardNumbers} alphanumeric · {preflight.missingCardNumber} unnumbered
                  </div>
                </div>
                <button
                  type="button"
                  className="hadmin-btn-success"
                  onClick={submitQueuedImport}
                  disabled={submitting || hasBlockingPreflightIssues}
                >
                  {submitting ? `Uploading ${stageProgress}%` : `Queue ${rows.length} Rows`}
                </button>
              </div>

              {hasBlockingPreflightIssues && (
                <div className="hadmin-error" style={{ marginTop: 12 }}>
                  Import blocked: {preflight.missingName} missing names, {preflight.missingCategory} missing categories,
                  {" "}{preflight.missingSubcategory} missing subcategories, and {preflight.duplicateGroups.length} duplicate identity groups. {preflight.invalidYearRanges} row(s) have an end year earlier than the start year.
                  {importMode === "package" && releasePreflight?.status === "review" ? " Release matching also requires review." : ""}
                  {importMode === "package" && preflight.duplicateGroups.length > 0 ? " Duplicate canonical identities remain blocked. The importer now treats additional_details.variation as an optional identity discriminator." : ""}
                </div>
              )}

              {preflight.missingYear > 0 && (
                <div className="hadmin-info" style={{ marginTop: 12 }}>
                  {preflight.missingYear} row(s) have no year. This is allowed, but confirm that is intentional.
                </div>
              )}
            </div>

            <div className="hadmin-table-wrap" style={{ marginTop: 14 }}>
              <table className="hadmin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Brand</th>
                    <th>Set</th>
                    <th>Year</th>
                    <th>End Year</th>
                    <th>Card #</th>
                    <th>Category</th>
                    <th>Subcategory</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => (
                    <tr key={`${row.name}-${row.card_number}-${index}`}>
                      <td>{row.name || <span className="hadmin-muted">Missing</span>}</td>
                      <td>{row.brand || "—"}</td>
                      <td>{row.set_name || "—"}</td>
                      <td>{row.year ?? "—"}</td>
                      <td>{row.year_end ?? "—"}</td>
                      <td>{row.card_number ?? "—"}</td>
                      <td>{row.category_name || row.category_id || "—"}</td>
                      <td>{row.subcategory_name || row.subcategory_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {activeJob && (
        <section className="hadmin-panel" style={{ marginTop: 18 }}>
          <div className="hadmin-section-heading">
            <div>
              <h2>Current Import</h2>
              <p>{activeJob.filename}</p>
            </div>
            <span className="hadmin-pill blue">{statusLabel(activeJob.status)}</span>
          </div>

          <div className="hadmin-card">
            <div className="hadmin-row-between">
              <div>
                <strong>{progress}%</strong>
                <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {activeJob.processed_rows || 0} / {activeJob.total_rows || 0} processed
                </div>
              </div>
              {ACTIVE_STATUSES.has(activeJob.status) && (
                <button
                  type="button"
                  className="hadmin-btn-secondary"
                  onClick={() => cancelJob(activeJob.id)}
                >
                  Cancel Job
                </button>
              )}
            </div>

            <div style={{ marginTop: 12, height: 8, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)" }}>
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "#4da3ff",
                  transition: "width 0.25s ease",
                }}
              />
            </div>

            <div className="hadmin-muted" style={{ marginTop: 12, fontSize: 12 }}>
              {activeJob.status === "queued" && "Queued in Supabase. You can leave this page now; the cron worker will pick it up."}
              {activeJob.status === "processing" && "Processing inside Postgres. This does not depend on the browser staying open."}
              {activeJob.status === "completed" && "Import completed without review or row errors."}
              {activeJob.status === "needs_review" && "Import finished, but one or more rows were deliberately held for review instead of guessing."}
              {activeJob.status === "failed" && (activeJob.error_message || "The worker encountered a job-level failure.")}
            </div>

            <div className="hadmin-table-wrap" style={{ marginTop: 14 }}>
              <table className="hadmin-table">
                <thead>
                  <tr>
                    <th>Matched</th>
                    <th>Repaired</th>
                    <th>Inserted</th>
                    <th>Review</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{activeJob.matched_rows || 0}</td>
                    <td>{activeJob.repaired_rows || 0}</td>
                    <td>{activeJob.inserted_rows || 0}</td>
                    <td>{activeJob.review_rows || 0}</td>
                    <td>{activeJob.error_rows || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {reviewRows.length > 0 && (
            <div className="hadmin-card" style={{ marginTop: 14 }}>
              <h3 style={{ marginTop: 0 }}>Rows Requiring Attention</h3>
              <div className="hadmin-list">
                {reviewRows.map((row) => (
                  <div className="hadmin-list-item" key={`${row.row_number}-${row.status}`}>
                    <div>
                      <strong>Spreadsheet row {row.row_number}: {row.normalized_data?.name || "Unnamed"}</strong>
                      <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 3 }}>
                        {row.error_message || row.result_message || row.action}
                      </div>
                      {row.candidate_item_ids?.length > 0 && (
                        <div className="hadmin-muted" style={{ fontSize: 12, marginTop: 3 }}>
                          Candidate item IDs: {row.candidate_item_ids.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <section className="hadmin-panel" style={{ marginTop: 18 }}>
        <div className="hadmin-section-heading">
          <div>
            <h2>Import History</h2>
            <p>These jobs persist in Supabase, including imports that finished while Hobbli was closed.</p>
          </div>
          <button type="button" className="hadmin-btn-secondary" onClick={loadHistory}>
            Refresh
          </button>
        </div>

        {history.length === 0 ? (
          <div className="hadmin-muted">No queued V2 catalog imports yet.</div>
        ) : (
          <div className="hadmin-table-wrap">
            <table className="hadmin-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Matched</th>
                  <th>Repaired</th>
                  <th>Inserted</th>
                  <th>Review</th>
                  <th>Errors</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {history.map((job) => (
                  <tr
                    key={job.id}
                    onClick={() => selectHistoryJob(job)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{job.filename}</td>
                    <td>{statusLabel(job.status)}</td>
                    <td>{jobProgress(job)}%</td>
                    <td>{job.matched_rows}</td>
                    <td>{job.repaired_rows}</td>
                    <td>{job.inserted_rows}</td>
                    <td>{job.review_rows}</td>
                    <td>{job.error_rows}</td>
                    <td>{formatDate(job.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
